import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, X } from 'lucide-react';
import type { AnimationItem } from 'lottie-web';
import './QuizWelcomeModal.css';

interface QuizWelcomeModalProps {
  onDismiss: () => void;
}

const QuizWelcomeModal: React.FC<QuizWelcomeModalProps> = ({ onDismiss }) => {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const quizLinkRef = useRef<HTMLAnchorElement>(null);
  const animationRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    let animation: AnimationItem | undefined;
    const motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');

    const updateMotion = () => {
      if (motionPreference.matches) {
        animation?.goToAndStop(0, true);
      } else {
        animation?.play();
      }
    };

    // Load the player only when the invitation appears after the splash.
    import('lottie-web').then(({ default: lottie }) => {
      if (cancelled || !animationRef.current) return;
      animation = lottie.loadAnimation({
        container: animationRef.current,
        renderer: 'svg',
        loop: true,
        autoplay: !motionPreference.matches,
        path: `${process.env.PUBLIC_URL}/animations/bird-pair-love.json`,
      });
    }).catch((error) => console.error('Failed to load quiz invitation animation', error));

    motionPreference.addEventListener('change', updateMotion);
    return () => {
      cancelled = true;
      motionPreference.removeEventListener('change', updateMotion);
      animation?.destroy();
    };
  }, []);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    const root = document.documentElement;
    const body = document.body;
    const scrollX = window.scrollX;
    const scrollY = window.scrollY;
    const scrollbarWidth = window.innerWidth - root.clientWidth;
    const previousRootOverflow = root.style.overflow;
    const previousScrollBehavior = root.style.scrollBehavior;
    const previousBodyStyles = {
      overflow: body.style.overflow,
      position: body.style.position,
      top: body.style.top,
      left: body.style.left,
      right: body.style.right,
      paddingRight: body.style.paddingRight,
    };

    // Fix the page in place as well as hiding overflow so touch scrolling on
    // iOS cannot move the blurred background. Keep the current scroll offset.
    const paddingRight = parseFloat(window.getComputedStyle(body).paddingRight) || 0;
    root.style.overflow = 'hidden';
    Object.assign(body.style, {
      overflow: 'hidden',
      position: 'fixed',
      top: `-${scrollY}px`,
      left: `-${scrollX}px`,
      right: '0',
      paddingRight: `${paddingRight + scrollbarWidth}px`,
    });
    dialog.showModal();
    quizLinkRef.current?.focus({ preventScroll: true });

    return () => {
      dialog.close();
      Object.assign(body.style, previousBodyStyles);
      root.style.overflow = previousRootOverflow;
      // Restore immediately even when the page normally uses smooth scrolling.
      root.style.scrollBehavior = 'auto';
      window.scrollTo(scrollX, scrollY);
      root.style.scrollBehavior = previousScrollBehavior;
    };
  }, []);

  return (
    <dialog
      ref={dialogRef}
      className="quiz-welcome-modal"
      aria-labelledby="quiz-welcome-title"
      aria-describedby="quiz-welcome-description"
      onCancel={(event) => {
        event.preventDefault();
        onDismiss();
      }}
    >
      <button
        type="button"
        className="quiz-welcome-close"
        aria-label="Close quiz invitation"
        onClick={onDismiss}
      >
        <X size={20} aria-hidden="true" />
      </button>
      <div ref={animationRef} className="quiz-welcome-animation" aria-hidden="true" />
      <span className="quiz-welcome-eyebrow">A little self-discovery</span>
      <h2 id="quiz-welcome-title">What’s your<br />attachment style?</h2>
      <p id="quiz-welcome-description">
        Understand your relationship patterns and take your first step toward healthier connections.
      </p>
      <Link
        ref={quizLinkRef}
        to="/attachment-style-quiz"
        className="quiz-welcome-cta"
        onClick={onDismiss}
      >
        Take the free attachment style quiz
        <ArrowUpRight size={20} aria-hidden="true" />
      </Link>
      <span className="quiz-welcome-detail">Free · About 5 minutes</span>
    </dialog>
  );
};

export default QuizWelcomeModal;
